import { useCallback, useEffect, useState } from 'react'
import { registerSW } from 'virtual:pwa-register'
import type { Measurement, Profile, Settings, TrainingProgram, WorkoutExerciseResult, WorkoutRecord } from '../types'
import { AppShell, type Tab } from '../components/AppShell'
import { TodayScreen } from '../screens/TodayScreen'
import { HistoryScreen } from '../screens/HistoryScreen'
import { ProgressScreen } from '../screens/ProgressScreen'
import { SettingsScreen } from '../screens/SettingsScreen'
import { getActiveProgram, getAllWorkoutExercises, getHistory, getMeasurements, getProfile, getSettings, getWorkoutByDate, initializeDatabase, requestPersistentStorage } from '../storage/db'
import { localDateKey, programDay } from '../utils/date'
import { createAnalysisExport } from '../importExport/analysis'
import { createFullBackup, markBackupCreated } from '../importExport/backup'
import { datedFilename, saveJsonFile } from '../importExport/file'

interface AppData {
  profile: Profile
  settings: Settings
  program: TrainingProgram
  workouts: WorkoutRecord[]
  results: WorkoutExerciseResult[]
  measurements: Measurement[]
  todayWorkout?: WorkoutRecord
}

function initialTab(): Tab {
  const value = new URLSearchParams(window.location.search).get('tab')
  return value === 'history' || value === 'progress' || value === 'settings' ? value : 'today'
}

export default function App() {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [data, setData] = useState<AppData>()
  const [error, setError] = useState('')
  const [offline, setOffline] = useState(!navigator.onLine)
  const [persistent, setPersistent] = useState<boolean | undefined>()
  const [updateReady, setUpdateReady] = useState(false)
  const [workoutActive, setWorkoutActive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [updateServiceWorker, setUpdateServiceWorker] = useState<((reloadPage?: boolean) => Promise<void>)>()

  const load = useCallback(async () => {
    try {
      await initializeDatabase()
      const [profile, settings, program, workouts, results, measurements, today] = await Promise.all([getProfile(), getSettings(), getActiveProgram(), getHistory(), getAllWorkoutExercises(), getMeasurements(), getWorkoutByDate(localDateKey())])
      if (!profile || !settings) throw new Error('Не удалось прочитать профиль или настройки')
      setData({ profile, settings, program, workouts, results, measurements, todayWorkout: today?.workout })
      setError('')
      document.documentElement.dataset.theme = settings.theme
      const dark = settings.theme === 'dark' || (settings.theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches)
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#101310' : '#f5f4ef')
    } catch (caught) {
      console.error(caught)
      setError('Локальная база временно недоступна. Перезапусти приложение; существующие данные не удалены.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { load(); requestPersistentStorage().then(setPersistent) }, [load])
  useEffect(() => {
    const online = () => setOffline(false)
    const offlineHandler = () => setOffline(true)
    window.addEventListener('online', online); window.addEventListener('offline', offlineHandler)
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offlineHandler) }
  }, [])
  useEffect(() => {
    const update = registerSW({
      onNeedRefresh() { setUpdateReady(true) },
      onRegisterError(caught) { if (import.meta.env.DEV) console.warn('Service Worker', caught) }
    })
    setUpdateServiceWorker(() => update)
  }, [])

  if (loading) return <div className="splash"><div className="splash__mark">PT</div><p>Готовим сегодняшнюю тренировку…</p></div>
  if (error || !data) return <div className="fatal-state"><h1>Не удалось открыть приложение</h1><p>{error}</p><button className="button button--primary" onClick={load}>Попробовать снова</button></div>

  const day = programDay(data.program)
  const exportAnalysis = () => saveJsonFile(createAnalysisExport(data.profile, data.program, data.workouts, data.results), datedFilename('training-analysis'))
  const backup = async () => { const filename = datedFilename('personal-trainer-backup'); await saveJsonFile(await createFullBackup(), filename); await markBackupCreated(filename); load() }
  const changeTab = (next: Tab) => {
    setTab(next)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  return <AppShell tab={tab} onTab={changeTab} offline={offline}>
    {tab === 'today' && <TodayScreen program={data.program} day={day} settings={data.settings} todayWorkout={data.todayWorkout} workouts={data.workouts} results={data.results} onChanged={load} onWorkoutActive={setWorkoutActive} onExport={exportAnalysis} onBackup={backup}/>} 
    {tab === 'history' && <HistoryScreen workouts={data.workouts} results={data.results} program={data.program}/>} 
    {tab === 'progress' && <ProgressScreen workouts={data.workouts} results={data.results} program={data.program} measurements={data.measurements}/>} 
    {tab === 'settings' && <SettingsScreen profile={data.profile} settings={data.settings} program={data.program} workouts={data.workouts} results={data.results} measurements={data.measurements} persistent={persistent} onChanged={load}/>} 
    {updateReady && !workoutActive && <div className="update-banner" role="status"><span><strong>Доступно обновление</strong><small>Можно установить сейчас</small></span><button onClick={()=>updateServiceWorker?.(true)}>Обновить</button><button aria-label="Позже" onClick={()=>setUpdateReady(false)}>×</button></div>}
  </AppShell>
}
